import type { ContractorTranslation } from './en';

export const es419: ContractorTranslation = {
  common: {
    cancel: 'Cancelar',
    save: 'Guardar',
    retry: 'Reintentar',
    download: 'Descargar',
    continue: 'Continuar',
    yes: 'Sí',
    no: 'No',
    genericError: 'Algo salió mal. Inténtalo de nuevo.',
    networkError: 'No se pudo conectar con DubForge. Revisa tu conexión a internet.',
  },
  app: {
    tagline: 'Asignaciones de doblaje, archivos y revisiones en un solo lugar.',
    startupFailed: 'DubForge no pudo iniciarse',
    notFoundTitle: 'Página no encontrada',
    notFoundBody: 'Esa página no existe o no tienes acceso a ella.',
    goHome: 'Ir al inicio',
    crashTitle: 'Algo salió mal',
    crashBody: 'Intenta recargar la página.',
    reload: 'Recargar',
  },
  language: {
    label: 'Idioma',
    change: 'Cambiar idioma',
  },
  welcome: {
    title: 'Te damos la bienvenida a DubForge',
    body: 'Elige tu idioma. DubForge y tus notificaciones de Discord lo usarán.',
    changeLater: 'Puedes cambiarlo en cualquier momento desde el menú de idioma, arriba a la derecha.',
  },
  nav: {
    myAssignments: 'Mis asignaciones',
    openMenu: 'Abrir menú',
    account: 'Cuenta',
    notificationEmail: 'Correo para notificaciones',
    signOut: 'Cerrar sesión',
    roleAdmin: 'Administración',
    roleContractor: 'Talento de doblaje',
    lightMode: 'Modo claro',
    darkMode: 'Modo oscuro',
  },
  login: {
    signIn: 'Iniciar sesión con Discord',
    onlyInvited: 'Solo pueden iniciar sesión las cuentas de Discord que agregó el dueño del canal.',
    signingIn: 'Iniciando sesión…',
    backToSignIn: 'Volver a iniciar sesión',
    failed: 'No se pudo iniciar sesión. Inténtalo de nuevo.',
  },
  authErrors: {
    not_invited: 'Esta cuenta de Discord no se ha agregado a DubForge. Pídele al dueño del canal que te agregue.',
    access_off: 'Se desactivó tu acceso a DubForge.',
    discord_only: 'Solo puedes iniciar sesión con Discord.',
  },
  errors: {
    not_signed_in: 'Tu sesión terminó. Vuelve a iniciar sesión.',
    access_off: 'Se desactivó tu acceso a DubForge.',
    admins_only: 'No tienes permiso para hacer eso.',
    not_found: 'Ese elemento no existe o ya no está disponible.',
    invalid_request: 'Algo en la solicitud no es válido. Revísalo e inténtalo de nuevo.',
    conflict: 'Eso ya existe.',
    already_approved: 'Esta asignación ya fue aprobada, así que no hace falta subir nada más.',
    not_audio: 'Solo se pueden subir archivos WAV o MP3. Sube solo tu pista de voz.',
    upload_incomplete: 'La subida no terminó. Inténtalo de nuevo.',
    discord_unreachable: 'No se pudo conectar con Discord. Inténtalo más tarde.',
    server_error: 'Algo salió mal de nuestro lado. Inténtalo de nuevo.',
  },
  notifications: {
    title: 'Notificaciones',
    openWithCount: 'Notificaciones ({{count}} sin leer)',
    markAllRead: 'Marcar todo como leído',
    empty: 'Todavía no hay nada.',
  },
  emailSettings: {
    body: 'Te enviamos un correo cuando recibes una nueva asignación y cuando se revisa tu trabajo.',
    label: 'Dirección de correo',
    fromDiscord: 'Se tomó de tu cuenta de Discord. Cámbialo si prefieres otra dirección.',
    saved: 'Se guardó la dirección de correo',
    invalid: 'Escribe una dirección de correo válida.',
    missing: 'Todavía no hay una dirección de correo. Agrega una para recibir notificaciones.',
  },
  status: {
    Assigned: 'Asignada',
    InProgress: 'En curso',
    Submitted: 'En espera de revisión',
    ChangesRequested: 'Cambios solicitados',
    Approved: 'Aprobada',
  },
  assetKind: {
    package: 'Paquete de doblaje (video + voz + SFX)',
    video: 'Video de referencia (720p)',
    sfx: 'Pista de SFX / música',
    voice: 'Pista de voz original',
    script: 'Guion',
    other: 'Otro',
  },
  translation: {
    machine: 'Traducción automática',
    showOriginal: 'Ver original',
    showTranslation: 'Ver traducción',
  },
  uploader: {
    progress: '{{percent}}% de {{size}}',
    queued: 'En espera para subir: {{count}}',
    keepOpen: 'Mantén esta pestaña abierta hasta que termine.',
    cancelled: 'Subida cancelada',
    done: 'Se subió {{name}}',
    failed: 'Error al subir: {{error}}',
  },
  my: {
    greeting: 'Hola, {{name}}',
    subtitle: 'Tus asignaciones de doblaje.',
    emptyTitle: 'Todavía no tienes asignaciones',
    emptyBody: 'Cuando te asignen un video, recibirás un mensaje en Discord y aparecerá aquí.',
    todo: 'Pendientes',
    waiting: 'En espera de revisión',
    done: 'Terminadas',
    updated: 'Actualizado {{when}}',
  },
  assignment: {
    back: 'Mis asignaciones',
    dubIn: 'Doblaje: {{language}}',
    paid: 'Pagado',
    next: {
      Assigned: 'Descarga los archivos de origen, graba tu voz y luego sube aquí tu pista de voz.',
      InProgress: 'Cuando tu pista de voz esté lista, súbela abajo.',
      Submitted: 'Tu doblaje se envió a revisión. Recibirás un mensaje en Discord cuando lo revisen.',
      ChangesRequested: 'Se solicitaron cambios. Lee los comentarios de abajo y luego sube una nueva versión.',
      Approved: 'Aprobado. ¡Gracias!',
    },
    notes: 'Notas',
    sourceFiles: 'Archivos de origen',
    noFiles: 'Todavía no se han subido archivos.',
    fileInfo: '{{kind}} · {{size}}',
    packageHint: 'Un solo .mp4 con el video y dos pistas de audio: la pista 1 son las líneas de voz originales y la pista 2 es el SFX.',
    uploadTitle: 'Sube tu pista de voz',
    voiceOnlyTitle: 'Solo tu voz',
    voiceOnlyBody:
      'Sube solo tu voz grabada: sin música, sin efectos de sonido y sin nada del audio original. Asegúrate de que dure lo mismo que el original.',
    voiceOnlyConfirm: 'Este archivo contiene solo líneas de voz.',
    dropLabel: 'Sube aquí tu pista de voz',
    dropHint: 'Solo WAV o MP3.',
    noteForReviewer: 'Nota para quien revisa (opcional)',
    lengthTitle: 'La duración no coincide',
    lengthBody:
      'Tu archivo dura {{dub}}, pero la pista de SFX dura {{sfx}}. Si tu pista empieza en otro punto o está cortada, no quedará sincronizada con el video. ¿Quieres subirla de todos modos?',
    uploadAnyway: 'Subir de todos modos',
    chooseAnother: 'Elegir otro archivo',
    versions: 'Versiones subidas',
    nothingUploaded: 'Todavía no se ha subido nada.',
    version: 'Versión {{version}}',
    reviewApproved: 'Aprobada',
    reviewChanges: 'Cambios solicitados',
    reviewPending: 'En espera de revisión',
    yourNote: 'Tu nota:',
    feedback: 'Comentarios',
    listen: 'Escuchar',
    uploadedOn: '{{date}} · {{size}}',
    length: 'Duración: {{duration}}',
  },
};
